import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Download, CheckCircle, AlertCircle, Play, Pause, Volume2,
  ChevronLeft, ArrowRight, FolderOpen, X, Columns2, Rows2,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import { UpgradeModal } from '@/components/subscription';
import LibraryPicker, { stagedFileName, type StagedLibraryFile } from '@/components/library/LibraryPicker';
import { useMovConversion } from '@/hooks/useMovConversion';

// File size limit for non-subscribers (10MB)
const MAX_FILE_SIZE_FREE = 10 * 1024 * 1024;

interface VideoFile {
  id: string;
  file: File;
  status: 'ready' | 'uploading' | 'uploaded' | 'processing' | 'processed' | 'error';
  progress: number;
  statusText?: string;
  downloadUrl?: string;
  s3Key?: string;
  isImported?: boolean; // true when picked from My Library (already staged in S3 — skip upload)
}

interface StitchResult {
  downloadUrl: string;
  fileName: string;
}

interface CompletedStitch {
  id: string;
  fileName: string;
  downloadUrl: string;
  originalFiles: {
    videoA: string;
    videoB: string;
  };
  layout: 'side-by-side' | 'top-bottom';
  completedAt: Date;
  size?: number;
}

interface StitchFormData {
  videoA: VideoFile | null;
  videoB: VideoFile | null;
  trimVideoA: number;
  trimVideoB: number;
  layout: 'side-by-side' | 'top-bottom';
  audioOnlyA: boolean;
  audioOnlyB: boolean;
}

interface VideoMetadata {
  duration: number;
  url: string;
  startTime?: Date;
  endTime?: Date;
}

// Utility function to parse filename and extract timestamps
const parseVideoFilename = (filename: string) => {
  // Format: {meeting_id}-{start_time}-to-{end_time}.mp4
  const match = filename.match(/^(\d+)-(.+)-to-(.+)\.mp4$/);
  if (!match) return null;
  
  const [, meetingId, startTimeStr, endTimeStr] = match;
  
  try {
    const startTime = new Date(startTimeStr.replace(/_/g, ':'));
    const endTime = new Date(endTimeStr.replace(/_/g, ':'));
    return { meetingId, startTime, endTime };
  } catch {
    return null;
  }
};

// Calculate auto-sync trim values
const calculateAutoSync = (videoAName: string, videoBName: string) => {
  const videoAData = parseVideoFilename(videoAName);
  const videoBData = parseVideoFilename(videoBName);
  
  if (!videoAData || !videoBData) return null;
  
  const timeDiffSeconds = Math.abs(videoAData.startTime.getTime() - videoBData.startTime.getTime()) / 1000;
  
  if (videoAData.startTime < videoBData.startTime) {
    return { trimVideoA: timeDiffSeconds, trimVideoB: 0 };
  } else {
    return { trimVideoA: 0, trimVideoB: timeDiffSeconds };
  }
};

const VideoStitcher = () => {
  const [formData, setFormData] = useState<StitchFormData>({
    videoA: null,
    videoB: null,
    trimVideoA: 0,
    trimVideoB: 0,
    layout: 'side-by-side',
    audioOnlyA: false,
    audioOnlyB: false
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<StitchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAutoSync, setIsAutoSync] = useState(true);
  const [completedStitches, setCompletedStitches] = useState<CompletedStitch[]>([]);
  const [videoMetadata, setVideoMetadata] = useState<{ videoA: VideoMetadata | null; videoB: VideoMetadata | null }>({
    videoA: null,
    videoB: null
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoSyncCalculated, setAutoSyncCalculated] = useState<{ trimVideoA: number; trimVideoB: number } | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [libraryPickerFor, setLibraryPickerFor] = useState<'videoA' | 'videoB' | null>(null);

  const videoARef = useRef<HTMLVideoElement>(null);
  const videoBRef = useRef<HTMLVideoElement>(null);
  const { toast } = useToast();
  const { isSubscriber } = useSubscription();
  const { getAuthHeaders } = useAuth();
  const { needsConversion, convert } = useMovConversion();

  const handleFileChange = (field: 'videoA' | 'videoB', file: File | null) => {
    if (file && !file.type.startsWith('video/')) {
      toast({
        title: "Invalid file",
        description: "Please select a video file only",
        variant: "destructive"
      });
      return;
    }

    // Check file size limit for non-subscribers - show upgrade modal
    if (file && !isSubscriber && file.size > MAX_FILE_SIZE_FREE) {
      setShowUpgradeModal(true);
      return;
    }

    // Create video URL for preview and VideoFile object
    let videoUrl = '';
    let videoFile: VideoFile | null = null;
    
    if (file) {
      videoUrl = URL.createObjectURL(file);
      videoFile = {
        id: Math.random().toString(36).substr(2, 9),
        file,
        status: 'ready',
        progress: 0,
        statusText: 'Ready to upload'
      };
    }
    
    setFormData(prev => ({ ...prev, [field]: videoFile }));
    setVideoMetadata(prev => ({
      ...prev,
      [field]: file ? { duration: 0, url: videoUrl } : null
    }));
  };

  // "Choose from your library" — the staged key is already in S3, so the slot
  // is marked isImported and handleSubmit skips the upload for it.
  const handleLibraryPick = (field: 'videoA' | 'videoB', f: StagedLibraryFile) => {
    const videoFile: VideoFile = {
      id: Math.random().toString(36).substr(2, 9),
      file: new File([], stagedFileName(f), { type: 'video/mp4' }),
      status: 'uploaded',
      progress: 100,
      statusText: 'From your library',
      s3Key: f.key,
      isImported: true,
    };
    setFormData(prev => ({ ...prev, [field]: videoFile }));
    setVideoMetadata(prev => ({
      ...prev,
      [field]: { duration: 0, url: f.downloadUrl }
    }));
  };

  const updateFileState = (field: 'videoA' | 'videoB', updates: Partial<VideoFile>) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field] ? { ...prev[field]!, ...updates } : null
    }));
  };

  const uploadToS3 = async (file: File, uploadUrl: string, onProgress: (progress: number) => void): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          onProgress(progress);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });
      
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  };

  // Handle video metadata loading
  const handleVideoLoadedMetadata = (field: 'videoA' | 'videoB', video: HTMLVideoElement) => {
    setVideoMetadata(prev => ({
      ...prev,
      [field]: prev[field] ? { ...prev[field]!, duration: video.duration } : null
    }));
  };

  // Auto-sync calculation when both videos are loaded
  useEffect(() => {
    if (formData.videoA && formData.videoB && isAutoSync) {
      const autoSync = calculateAutoSync(formData.videoA.file.name, formData.videoB.file.name);
      if (autoSync) {
        setAutoSyncCalculated(autoSync);
        setFormData(prev => ({
          ...prev,
          trimVideoA: autoSync.trimVideoA,
          trimVideoB: autoSync.trimVideoB
        }));
      } else {
        setAutoSyncCalculated(null);
        setIsAutoSync(false); // Switch to manual mode if auto-sync fails
      }
    }
  }, [formData.videoA, formData.videoB, isAutoSync]);

  // Synchronized video playback
  const togglePlayback = () => {
    if (!videoARef.current || !videoBRef.current) return;
    
    if (isPlaying) {
      videoARef.current.pause();
      videoBRef.current.pause();
    } else {
      // Set current time based on trim values before playing
      videoARef.current.currentTime = formData.trimVideoA;
      videoBRef.current.currentTime = formData.trimVideoB;
      
      videoARef.current.play();
      videoBRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Handle slider changes for manual sync
  const handleSliderChange = (field: 'trimVideoA' | 'trimVideoB', value: number[]) => {
    const trimValue = value[0];
    setFormData(prev => ({ ...prev, [field]: trimValue }));
    
    // Update video current time
    if (field === 'trimVideoA' && videoARef.current) {
      videoARef.current.currentTime = trimValue;
    } else if (field === 'trimVideoB' && videoBRef.current) {
      videoBRef.current.currentTime = trimValue;
    }
  };

  const handleNumberChange = (field: 'trimVideoA' | 'trimVideoB', value: string) => {
    const numValue = Math.max(0, parseFloat(value) || 0);
    setFormData(prev => ({ ...prev, [field]: numValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.videoA || !formData.videoB) {
      toast({
        title: "Missing videos",
        description: "Please select both Video A and Video B",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      toast({
        title: "Processing started",
        description: "Uploading and stitching videos..."
      });

      // Upload Video A to S3 (library picks are already staged — skip upload)
      let keyA: string;
      if (formData.videoA.isImported && formData.videoA.s3Key) {
        keyA = formData.videoA.s3Key;
      } else {
        updateFileState('videoA', {
          status: 'uploading',
          progress: 0,
          statusText: 'Getting upload URL...'
        });

        const uploadResponseA = await fetch('/get-upload-url', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            fileName: formData.videoA.file.name,
            fileType: formData.videoA.file.type,
            ephemeral: true
          })
        });

        if (!uploadResponseA.ok) {
          throw new Error('Failed to get upload URL for Video A');
        }

        const { uploadUrl: uploadUrlA, key: keyA_ } = await uploadResponseA.json();
        keyA = keyA_;

        updateFileState('videoA', {
          statusText: 'Uploading Video A to S3...',
          s3Key: keyA
        });

        await uploadToS3(formData.videoA.file, uploadUrlA, (progress) => {
          updateFileState('videoA', {
            progress,
            statusText: `Uploading Video A... ${progress}%`
          });
        });
      }

      // Convert non-MP4 files (e.g. iPhone MOV) via MediaConvert
      if (needsConversion(formData.videoA.file.name)) {
        updateFileState('videoA', { statusText: 'Converting MOV to MP4...' });
        const result = await convert(keyA);
        keyA = result.outputKey;
        updateFileState('videoA', { s3Key: keyA });
      }

      updateFileState('videoA', {
        status: 'uploaded',
        progress: 100,
        statusText: 'Video A uploaded'
      });

      // Upload Video B to S3 (library picks are already staged — skip upload)
      let keyB: string;
      if (formData.videoB.isImported && formData.videoB.s3Key) {
        keyB = formData.videoB.s3Key;
      } else {
        updateFileState('videoB', {
          status: 'uploading',
          progress: 0,
          statusText: 'Getting upload URL...'
        });

        const uploadResponseB = await fetch('/get-upload-url', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            fileName: formData.videoB.file.name,
            fileType: formData.videoB.file.type,
            ephemeral: true
          })
        });

        if (!uploadResponseB.ok) {
          throw new Error('Failed to get upload URL for Video B');
        }

        const { uploadUrl: uploadUrlB, key: keyB_ } = await uploadResponseB.json();
        keyB = keyB_;

        updateFileState('videoB', {
          statusText: 'Uploading Video B to S3...',
          s3Key: keyB
        });

        await uploadToS3(formData.videoB.file, uploadUrlB, (progress) => {
          updateFileState('videoB', {
            progress,
            statusText: `Uploading Video B... ${progress}%`
          });
        });
      }

      // Convert non-MP4 files (e.g. iPhone MOV) via MediaConvert
      if (needsConversion(formData.videoB.file.name)) {
        updateFileState('videoB', { statusText: 'Converting MOV to MP4...' });
        const result = await convert(keyB);
        keyB = result.outputKey;
        updateFileState('videoB', { s3Key: keyB });
      }

      updateFileState('videoB', {
        status: 'uploaded',
        progress: 100,
        statusText: 'Video B uploaded'
      });

      // Process videos
      updateFileState('videoA', { status: 'processing', statusText: 'Stitching videos...' });
      updateFileState('videoB', { status: 'processing', statusText: 'Stitching videos...' });

      const response = await fetch('/api/stitch-auto', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          videoA: keyA,
          videoB: keyB,
          trimVideoA: formData.trimVideoA,
          trimVideoB: formData.trimVideoB,
          layout: formData.layout,
          audioOnlyA: formData.audioOnlyA,
          audioOnlyB: formData.audioOnlyB
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to stitch videos: ${response.statusText}`);
      }

      const data = await response.json();

      // If async job was started, poll for completion
      let result = data;
      if (data.jobId && !data.downloadUrl) {
        updateFileState('videoA', { statusText: 'Processing large files...' });
        updateFileState('videoB', { statusText: 'Processing large files...' });

        // Poll for job completion
        let attempts = 0;
        const maxAttempts = 120; // 10 minutes max
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 5000));
          attempts++;

          try {
            const statusRes = await fetch(`/api/stitch-status/${data.jobId}`);
            if (!statusRes.ok) {
              console.warn(`Status check returned ${statusRes.status}, retrying...`);
              continue;
            }
            const statusData = await statusRes.json();

            if (statusData.status === 'completed' && statusData.downloadUrl) {
              result = { downloadUrl: statusData.downloadUrl, fileName: statusData.fileName || 'stitched_video.mp4' };
              break;
            } else if (statusData.status === 'failed') {
              throw new Error(statusData.error || 'Stitching failed');
            }
          } catch (pollError) {
            // Re-throw actual stitch failures (from the 'failed' status check above)
            if (pollError instanceof Error && !(pollError instanceof SyntaxError) && !(pollError instanceof TypeError)) {
              throw pollError;
            }
            // SyntaxError = non-JSON response (ALB 502), TypeError = network error — retry
            console.warn('Status poll error, retrying...', pollError);
            continue;
          }

          updateFileState('videoA', { statusText: `Processing... (${Math.round(attempts * 5 / 60)}min)` });
        }

        if (attempts >= maxAttempts) {
          throw new Error('Processing timed out');
        }
      }
      
      updateFileState('videoA', { status: 'processed', statusText: 'Completed!' });
      updateFileState('videoB', { status: 'processed', statusText: 'Completed!' });
      
      const stitchResult = {
        downloadUrl: result.downloadUrl,
        fileName: result.fileName || 'stitched_video.mp4'
      };
      
      setResult(stitchResult);

      // Add to completed stitches list
      const completedStitch: CompletedStitch = {
        id: Date.now().toString(),
        fileName: stitchResult.fileName,
        downloadUrl: stitchResult.downloadUrl,
        originalFiles: {
          videoA: formData.videoA?.file.name || 'Video A',
          videoB: formData.videoB?.file.name || 'Video B'
        },
        layout: formData.layout,
        completedAt: new Date(),
      };
      
      setCompletedStitches(prev => [completedStitch, ...prev]);

      toast({
        title: "Success!",
        description: "Videos have been stitched successfully",
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      updateFileState('videoA', { status: 'error', statusText: `Error: ${errorMessage}` });
      updateFileState('videoB', { status: 'error', statusText: `Error: ${errorMessage}` });
      toast({
        title: "Stitching failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadVideo = async (downloadUrl?: string, fileName?: string) => {
    const url = downloadUrl || result?.downloadUrl;
    const name = fileName || result?.fileName;
    
    if (!url || !name) return;
    
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 1000);
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: "The download link may have expired",
        variant: "destructive"
      });
    }
  };

  const removeCompletedStitch = (id: string) => {
    setCompletedStitches(prev => prev.filter(stitch => stitch.id !== id));
  };

  const clearAllCompleted = () => {
    setCompletedStitches([]);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const audioOnlyMode = formData.audioOnlyA || formData.audioOnlyB;
  const totalSize = (formData.videoA?.file?.size || 0) + (formData.videoB?.file?.size || 0);

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: 1440,
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
        <Link to="/" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>Stitcher</span>
      </div>

      {/* Header */}
      <header
        style={{
          padding: '10px 0 28px',
          borderBottom: '1px solid var(--hj-hairline-soft)',
          marginBottom: 28,
        }}
      >
        <div className="mono-label" style={{ marginBottom: 10 }}>Stitcher</div>
        <h1
          className="font-hj-display m-0"
          style={{
            fontSize: 'clamp(36px, 4.6vw, 56px)',
            lineHeight: 1,
            color: 'var(--hj-ink)',
            letterSpacing: '-0.025em',
          }}
        >
          Side-by-side. Top&#8209;and&#8209;bottom.
        </h1>
        <p
          className="m-0"
          style={{
            marginTop: 12,
            color: 'var(--hj-ink-3)',
            fontSize: 14.5,
            maxWidth: 600,
            lineHeight: 1.5,
          }}
        >
          Drop two clips, line them up, render the split-screen. Audio-only mode mixes one source's track into the other's video.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5">
        <section className="flex flex-col gap-4">
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5"
            style={{
              padding: 22,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            {/* Two source slots */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SourceSlot
                slot="A"
                file={formData.videoA}
                audioOnly={formData.audioOnlyA}
                onAudioToggle={(checked) => setFormData(prev => ({ ...prev, audioOnlyA: checked }))}
                onPick={(f) => handleFileChange('videoA', f)}
                onLibrary={() => setLibraryPickerFor('videoA')}
                formatFileSize={formatFileSize}
              />
              <SourceSlot
                slot="B"
                file={formData.videoB}
                audioOnly={formData.audioOnlyB}
                onAudioToggle={(checked) => setFormData(prev => ({ ...prev, audioOnlyB: checked }))}
                onPick={(f) => handleFileChange('videoB', f)}
                onLibrary={() => setLibraryPickerFor('videoB')}
                formatFileSize={formatFileSize}
              />
            </div>

            {/* Sync */}
            {formData.videoA && formData.videoB && (
              <div
                style={{
                  padding: 16,
                  borderRadius: 'var(--hj-r-sm)',
                  background: 'var(--hj-surface-2)',
                  border: '1px solid var(--hj-hairline-soft)',
                }}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                  <div>
                    <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Sync mode</div>
                    <p
                      className="m-0"
                      style={{ marginTop: 4, fontSize: 13, color: 'var(--hj-ink-2)' }}
                    >
                      {isAutoSync
                        ? 'Match by filename timestamps when available'
                        : 'Align manually with previews and sliders'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span style={{ fontSize: 12, color: 'var(--hj-ink-3)' }}>Manual</span>
                    <Switch id="sync-mode" checked={isAutoSync} onCheckedChange={setIsAutoSync} />
                    <span style={{ fontSize: 12, color: 'var(--hj-ink-3)' }}>Auto</span>
                  </div>
                </div>

                {isAutoSync && autoSyncCalculated && (
                  <InlineNote tone="success" icon={<CheckCircle size={14} />}>
                    Auto-sync: A trim {autoSyncCalculated.trimVideoA.toFixed(1)}s · B trim {autoSyncCalculated.trimVideoB.toFixed(1)}s
                  </InlineNote>
                )}

                {!isAutoSync && (
                  <div className="flex flex-col gap-4" style={{ marginTop: 12 }}>
                    <InlineNote tone="warn" icon={<AlertCircle size={14} />}>
                      Auto-sync unavailable. Use the sliders below to line up audio manually.
                    </InlineNote>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <PreviewPane
                        label="Video A"
                        videoRef={videoARef}
                        src={videoMetadata.videoA?.url}
                        onLoad={(el) => handleVideoLoadedMetadata('videoA', el)}
                        trimLabel="Trim from start"
                        trimValue={formData.trimVideoA}
                        trimMax={videoMetadata.videoA?.duration || 100}
                        onTrim={(v) => handleSliderChange('trimVideoA', v)}
                      />
                      <PreviewPane
                        label="Video B"
                        videoRef={videoBRef}
                        src={videoMetadata.videoB?.url}
                        onLoad={(el) => handleVideoLoadedMetadata('videoB', el)}
                        trimLabel="Trim from start"
                        trimValue={formData.trimVideoB}
                        trimMax={videoMetadata.videoB?.duration || 100}
                        onTrim={(v) => handleSliderChange('trimVideoB', v)}
                      />
                    </div>

                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={togglePlayback}
                        className="inline-flex items-center gap-2"
                        style={{
                          padding: '9px 18px',
                          borderRadius: 999,
                          background: 'transparent',
                          border: '1px solid var(--hj-hairline)',
                          color: 'var(--hj-ink-2)',
                          fontSize: 13,
                          fontWeight: 500,
                          cursor: 'pointer',
                          minHeight: 44,
                        }}
                      >
                        {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                        {isPlaying ? 'Pause both' : 'Play both'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Manual trim numeric inputs */}
            {(!isAutoSync || !autoSyncCalculated) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TrimNumberField
                  label="Trim Video A"
                  value={formData.trimVideoA}
                  onChange={(v) => handleNumberChange('trimVideoA', v)}
                />
                <TrimNumberField
                  label="Trim Video B"
                  value={formData.trimVideoB}
                  onChange={(v) => handleNumberChange('trimVideoB', v)}
                />
              </div>
            )}

            {/* Layout */}
            {!audioOnlyMode && (
              <div className="flex flex-col gap-2">
                <Label className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Layout</Label>
                <div className="flex gap-2">
                  <LayoutOption
                    active={formData.layout === 'side-by-side'}
                    onClick={() => setFormData(prev => ({ ...prev, layout: 'side-by-side' }))}
                    icon={<Columns2 size={16} />}
                    label="Side by side"
                  />
                  <LayoutOption
                    active={formData.layout === 'top-bottom'}
                    onClick={() => setFormData(prev => ({ ...prev, layout: 'top-bottom' }))}
                    icon={<Rows2 size={16} />}
                    label="Top / bottom"
                  />
                </div>
              </div>
            )}

            {audioOnlyMode && (
              <InlineNote tone="info" icon={<Volume2 size={14} />}>
                {formData.audioOnlyA && formData.audioOnlyB
                  ? 'Both clips set to audio only — output will be a WAV with both tracks mixed.'
                  : formData.audioOnlyA
                    ? "Video A audio only — output uses Video B's picture with both tracks mixed."
                    : "Video B audio only — output uses Video A's picture with both tracks mixed."}
              </InlineNote>
            )}

            <div className="flex items-center justify-end" style={{ gap: 12 }}>
              <button
                type="submit"
                disabled={!formData.videoA || !formData.videoB || isProcessing}
                className="inline-flex items-center gap-2 transition-opacity disabled:cursor-not-allowed"
                style={{
                  padding: '11px 22px',
                  borderRadius: 999,
                  background: 'var(--hj-blue)',
                  border: '1px solid var(--hj-blue)',
                  color: 'oklch(0.18 0.005 70)',
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  cursor: !formData.videoA || !formData.videoB || isProcessing ? 'not-allowed' : 'pointer',
                  opacity: !formData.videoA || !formData.videoB || isProcessing ? 0.5 : 1,
                  minHeight: 44,
                }}
              >
                {isProcessing ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Stitching…
                  </>
                ) : (
                  <>
                    Stitch
                    <ArrowRight size={14} strokeWidth={2.4} />
                  </>
                )}
              </button>
            </div>

            {error && (
              <InlineNote tone="error" icon={<AlertCircle size={14} />}>
                {error}
              </InlineNote>
            )}

            {result && (
              <div
                className="flex items-center justify-between gap-3"
                style={{
                  padding: 12,
                  borderRadius: 'var(--hj-r-sm)',
                  background: 'oklch(0.42 0.15 145 / 0.10)',
                  border: '1px solid oklch(0.42 0.15 145 / 0.45)',
                  color: 'var(--hj-ink)',
                  fontSize: 13,
                }}
              >
                <span className="inline-flex items-center gap-2">
                  <CheckCircle size={14} style={{ color: 'var(--hj-green)' }} />
                  Stitch complete.
                </span>
                <button
                  type="button"
                  onClick={() => downloadVideo()}
                  className="inline-flex items-center gap-1.5"
                  style={{
                    padding: '7px 14px',
                    borderRadius: 999,
                    background: 'var(--hj-blue)',
                    border: '1px solid var(--hj-blue)',
                    color: 'oklch(0.18 0.005 70)',
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <Download size={13} />
                  Download
                </button>
              </div>
            )}
          </form>

          {/* Session History */}
          {completedStitches.length > 0 && (
            <div
              style={{
                padding: 22,
                borderRadius: 'var(--hj-r-md)',
                background: 'var(--hj-surface-1)',
                border: '1px solid var(--hj-hairline)',
              }}
            >
              <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Session history</div>
                <button
                  type="button"
                  onClick={clearAllCompleted}
                  className="inline-flex items-center gap-1.5"
                  style={{
                    padding: '6px 12px',
                    borderRadius: 999,
                    background: 'transparent',
                    border: '1px solid var(--hj-hairline)',
                    color: 'var(--hj-ink-3)',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              </div>
              <div className="space-y-2">
                {completedStitches.map((stitch) => (
                  <div
                    key={stitch.id}
                    className="flex items-center gap-3"
                    style={{
                      padding: 12,
                      borderRadius: 'var(--hj-r-sm)',
                      background: 'oklch(0.42 0.15 145 / 0.10)',
                      border: '1px solid oklch(0.42 0.15 145 / 0.45)',
                    }}
                  >
                    <CheckCircle size={16} style={{ color: 'var(--hj-green)' }} />
                    <div className="flex-1 min-w-0">
                      <p
                        className="truncate m-0"
                        style={{ fontSize: 13, color: 'var(--hj-ink)', fontWeight: 500 }}
                      >
                        {stitch.fileName}
                      </p>
                      <div
                        className="truncate"
                        style={{ marginTop: 2, fontSize: 11, color: 'var(--hj-ink-3)' }}
                      >
                        {stitch.originalFiles.videoA} + {stitch.originalFiles.videoB}
                        <span style={{ marginLeft: 8, color: 'var(--hj-ink-4)' }}>
                          {stitch.layout === 'side-by-side' ? 'Side by side' : 'Top / bottom'} · {stitch.completedAt.toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => downloadVideo(stitch.downloadUrl, stitch.fileName)}
                      className="inline-flex items-center gap-1.5"
                      style={{
                        padding: '7px 14px',
                        borderRadius: 999,
                        background: 'var(--hj-blue)',
                        border: '1px solid var(--hj-blue)',
                        color: 'oklch(0.18 0.005 70)',
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <Download size={13} />
                      Download
                    </button>
                    <button
                      type="button"
                      onClick={() => removeCompletedStitch(stitch.id)}
                      aria-label="Remove"
                      style={{
                        background: 'transparent',
                        border: 0,
                        color: 'var(--hj-ink-4)',
                        cursor: 'pointer',
                        padding: 4,
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <aside className="flex flex-col gap-3">
          <StatTile label="Source clips" value={(formData.videoA ? 1 : 0) + (formData.videoB ? 1 : 0)} />
          <StatTile
            label="Uploaded"
            value={
              (formData.videoA && formData.videoA.status === 'uploaded' ? 1 : 0) +
              (formData.videoB && formData.videoB.status === 'uploaded' ? 1 : 0)
            }
          />
          <StatTile label="Processed" value={completedStitches.length} />
          <StatTile label="Total size" value={formatFileSize(totalSize)} />
        </aside>
      </div>

      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} trigger="video-tools" />

      <LibraryPicker
        open={libraryPickerFor !== null}
        onClose={() => setLibraryPickerFor(null)}
        onPick={(f) => { if (libraryPickerFor) handleLibraryPick(libraryPickerFor, f); }}
      />
    </main>
  );
};

// ── Helper presentational pieces ──────────────────────────────────────

const SourceSlot: React.FC<{
  slot: 'A' | 'B';
  file: VideoFile | null;
  audioOnly: boolean;
  onAudioToggle: (checked: boolean) => void;
  onPick: (file: File | null) => void;
  onLibrary: () => void;
  formatFileSize: (bytes: number) => string;
}> = ({ slot, file, audioOnly, onAudioToggle, onPick, onLibrary, formatFileSize }) => {
  const inputId = `video${slot}`;
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 'var(--hj-r-sm)',
        background: 'var(--hj-surface-2)',
        border: '1px solid var(--hj-hairline-soft)',
      }}
    >
      <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>
        Video {slot}
      </div>

      <label
        htmlFor={inputId}
        className="flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors"
        style={{
          marginTop: 10,
          padding: '20px 12px',
          borderRadius: 'var(--hj-r-sm)',
          border: `1.5px dashed ${file ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
          background: file ? 'var(--hj-blue-tint)' : 'var(--hj-surface-3)',
        }}
      >
        <FolderOpen size={20} style={{ color: 'var(--hj-ink-2)' }} />
        <span style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', textAlign: 'center' }}>
          {file ? 'Replace file' : 'Click to choose a video'}
        </span>
        <input
          id={inputId}
          type="file"
          accept="video/*"
          onChange={(e) => onPick(e.target.files?.[0] || null)}
          style={{ display: 'none' }}
        />
      </label>

      <button
        type="button"
        onClick={onLibrary}
        className="inline-flex items-center gap-1.5"
        style={{
          marginTop: 8,
          padding: '6px 10px',
          borderRadius: 'var(--hj-r-sm)',
          border: '1px solid var(--hj-hairline)',
          background: 'transparent',
          color: 'var(--hj-ink-2)',
          fontSize: 12,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        <FolderOpen size={13} strokeWidth={1.8} />
        Choose from your library
      </button>

      {file && (
        <div style={{ marginTop: 10 }}>
          <p
            className="truncate m-0"
            style={{ fontSize: 12.5, color: 'var(--hj-ink)', fontWeight: 500 }}
          >
            {file.file.name}
          </p>
          <div style={{ marginTop: 2, fontSize: 11, color: 'var(--hj-ink-3)' }}>
            {file.file.size > 0 ? `${formatFileSize(file.file.size)} · ` : ''}{file.statusText || file.status}
          </div>
          {file.progress > 0 && file.status !== 'processed' && (
            <Progress value={file.progress} className="h-1 mt-2" />
          )}
          <div className="flex items-center gap-2" style={{ marginTop: 10 }}>
            <Switch id={`audioOnly${slot}`} checked={audioOnly} onCheckedChange={onAudioToggle} />
            <Label
              htmlFor={`audioOnly${slot}`}
              className="cursor-pointer inline-flex items-center gap-1"
              style={{ fontSize: 12, color: 'var(--hj-ink-2)' }}
            >
              <Volume2 size={11} />
              Audio only
            </Label>
          </div>
        </div>
      )}
    </div>
  );
};

const PreviewPane: React.FC<{
  label: string;
  videoRef: React.RefObject<HTMLVideoElement>;
  src?: string;
  onLoad: (el: HTMLVideoElement) => void;
  trimLabel: string;
  trimValue: number;
  trimMax: number;
  onTrim: (v: number[]) => void;
}> = ({ label, videoRef, src, onLoad, trimLabel, trimValue, trimMax, onTrim }) => (
  <div className="flex flex-col gap-2">
    <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{label}</div>
    <video
      ref={videoRef}
      src={src}
      className="w-full"
      style={{
        borderRadius: 'var(--hj-r-sm)',
        border: '1px solid var(--hj-hairline)',
        background: 'oklch(0 0 0)',
      }}
      onLoadedMetadata={(e) => onLoad(e.currentTarget)}
      muted
    />
    <div style={{ fontSize: 11, color: 'var(--hj-ink-3)' }}>
      {trimLabel}: <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', color: 'var(--hj-ink)' }}>{trimValue.toFixed(1)}s</span>
    </div>
    <Slider
      value={[trimValue]}
      onValueChange={onTrim}
      max={trimMax}
      step={0.1}
      className="w-full"
    />
  </div>
);

const TrimNumberField: React.FC<{
  label: string;
  value: number;
  onChange: (v: string) => void;
}> = ({ label, value, onChange }) => (
  <div className="flex flex-col gap-2">
    <Label className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{label}</Label>
    <Input
      type="number"
      min="0"
      step="0.1"
      placeholder="0"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
    />
    <p className="m-0" style={{ fontSize: 11, color: 'var(--hj-ink-4)' }}>
      Seconds removed from the start.
    </p>
  </div>
);

const LayoutOption: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-2 transition-colors flex-1 justify-center"
    style={{
      padding: '10px 14px',
      borderRadius: 'var(--hj-r-sm)',
      background: active ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
      border: `1px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
      color: active ? 'var(--hj-blue)' : 'var(--hj-ink-2)',
      fontSize: 13,
      fontWeight: active ? 600 : 500,
      cursor: 'pointer',
      minHeight: 44,
    }}
  >
    {icon}
    {label}
  </button>
);

const InlineNote: React.FC<{
  tone: 'info' | 'success' | 'warn' | 'error';
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ tone, icon, children }) => {
  const palette = {
    info: { bg: 'var(--hj-blue-tint)', border: 'var(--hj-blue)', fg: 'var(--hj-ink)' },
    success: { bg: 'oklch(0.42 0.15 145 / 0.12)', border: 'oklch(0.55 0.16 145)', fg: 'var(--hj-ink)' },
    warn: { bg: 'oklch(0.65 0.16 75 / 0.16)', border: 'oklch(0.65 0.16 75)', fg: 'var(--hj-ink)' },
    error: { bg: 'oklch(0.30 0.12 25 / 0.18)', border: 'hsl(var(--destructive) / 0.5)', fg: 'hsl(var(--destructive))' },
  }[tone];
  return (
    <div
      className="flex items-start gap-2"
      style={{
        padding: 12,
        borderRadius: 'var(--hj-r-sm)',
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.fg,
        fontSize: 12.5,
        lineHeight: 1.45,
      }}
    >
      {icon && <span style={{ marginTop: 1, flexShrink: 0 }}>{icon}</span>}
      <span>{children}</span>
    </div>
  );
};

const StatTile: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div
    style={{
      padding: 16,
      borderRadius: 'var(--hj-r-md)',
      background: 'var(--hj-surface-1)',
      border: '1px solid var(--hj-hairline)',
    }}
  >
    <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{label}</div>
    <div
      className="tabular"
      style={{
        marginTop: 6,
        fontSize: 26,
        fontFamily: 'var(--hj-font-mono)',
        fontWeight: 600,
        color: 'var(--hj-ink)',
        lineHeight: 1,
      }}
    >
      {value}
    </div>
  </div>
);

export default VideoStitcher;